/**
 * TradingView-style drawing primitives for Lightweight Charts v5.
 *
 * Each primitive supports:
 *  - Hover highlighting with glow
 *  - Selection with visible handles
 *  - Dragging (whole drawing or individual handles)
 *  - Live preview during creation
 *  - Hit testing for mouse interaction
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8; // px radius for handle hit test
const HOVER_ALPHA = 0.35;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  const r = HANDLE_RADIUS * hpr;
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, r, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5 * hpr;
  ctx.stroke();
}

function hexToRgba(hex, alpha) {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

// ─── Base ─────────────────────────────────────────────────────────

class BasePrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
    this.isHovered = false;
    this.isSelected = false;
    this.lineWidth = 2;
    this.lineStyle = 0; // 0=solid, 1=dashed, 2=dotted
  }
  attached({ requestUpdate, chart, series }) {
    this._requestUpdate = requestUpdate;
    this._chart = chart;
    this._series = series;
  }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }

  /** px distance from point to this drawing */
  hitTest(x, y) { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }

  /**
   * Returns handle index if (x,y) is near a handle, else -1.
   * Override in subclass.
   */
  hitTestHandle(x, y) { return -1; }

  /**
   * Move entire drawing by delta in logical/price space.
   * Override in subclass.
   */
  moveBy(dLogical, dPrice) {}

  /**
   * Move a specific handle to a new position.
   * Override in subclass.
   */
  moveHandle(index, logical, price) {}
}

// ═══════════════════════════════════════════════════════════════════
// HORIZONTAL LINE
// ═══════════════════════════════════════════════════════════════════

class HLinePaneView {
  constructor(src) { this._s = src; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (target) => {
      target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
        const y = s._y;
        if (y == null) return;
        const W = bitmapSize.width;
        const yy = y * vpr;

        ctx.save();

        if (s.isHovered || s.isSelected) {
          ctx.strokeStyle = hexToRgba(s.color, HOVER_ALPHA);
          ctx.lineWidth = 6 * vpr;
          ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(W, yy); ctx.stroke();
        }

        ctx.strokeStyle = hexToRgba(s.color, s.isHovered || s.isSelected ? 1 : 0.8);
        const drawW = (s.lineWidth || 2) * vpr;
        ctx.lineWidth = s.isSelected ? drawW + vpr : drawW;
        if (s.lineStyle === 2) ctx.setLineDash([2 * hpr, 3 * hpr]);
        else if (s.lineStyle === 1 || s.lineStyle === undefined) ctx.setLineDash([6 * hpr, 4 * hpr]);
        ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(W, yy); ctx.stroke();

        // Price label
        const txt = s.price.toFixed(5);
        ctx.font = `${11 * vpr}px -apple-system, sans-serif`;
        const tw = ctx.measureText(txt).width;
        const lw = tw + 10 * hpr, lh = 18 * vpr;
        const lx = W - lw - 2 * hpr;
        ctx.fillStyle = s.color;
        ctx.beginPath(); ctx.roundRect(lx, yy - lh / 2, lw, lh, 3 * hpr); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
        ctx.fillText(txt, lx + 5 * hpr, yy);

        if (s.isSelected) {
          drawHandle(ctx, 40, y, hpr, vpr);
          drawHandle(ctx, W / hpr - 40, y, hpr, vpr);
        }
        ctx.restore();
      });
    }};
  }
}

export class HorizontalLinePrimitive extends BasePrimitive {
  constructor(price, options = {}) {
    super();
    this.price = price;
    this.color = options.color || '#f59e0b';
    if (options.lineWidth != null) this.lineWidth = options.lineWidth;
    if (options.lineStyle != null) this.lineStyle = options.lineStyle;
    this._y = null;
    this._view = new HLinePaneView(this);
  }
  updateAllViews() {
    if (this._series) this._y = this._series.priceToCoordinate(this.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) { return this._y != null ? Math.abs(y - this._y) : Infinity; }
  hitTestHandle(x, y) {
    if (this._y == null) return -1;
    if (Math.abs(y - this._y) < HANDLE_HIT && (x < 48 || x > 200)) return 0;
    return -1;
  }
  moveBy(dLogical, dPrice) { this.price += dPrice; this._requestUpdate?.(); }
  moveHandle(index, logical, price) { this.price = price; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// TREND LINE / RAY
// ═══════════════════════════════════════════════════════════════════

class TrendLinePaneView {
  constructor(src) { this._s = src; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (target) => {
      target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
        const p1 = s._p1, p2 = s._p2;
        if (!p1 || p1.x == null || !p2 || p2.x == null) return;

        ctx.save();
        const x1 = p1.x * hpr, y1 = p1.y * vpr;
        const x2 = p2.x * hpr, y2 = p2.y * vpr;

        let endX = x2, endY = y2;
        if (s.extend) {
          const dx = x2 - x1, dy = y2 - y1;
          const len = Math.hypot(dx, dy);
          if (len > 0) { const sc = bitmapSize.width * 3 / len; endX = x1 + dx * sc; endY = y1 + dy * sc; }
        }

        if (s.isHovered || s.isSelected) {
          ctx.strokeStyle = hexToRgba(s.color, HOVER_ALPHA);
          ctx.lineWidth = 8 * vpr; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(endX, endY); ctx.stroke();
        }

        ctx.strokeStyle = s.color;
        const lw = (s.lineWidth || 2) * vpr;
        ctx.lineWidth = s.isSelected ? lw + vpr : lw; ctx.lineCap = 'round';
        if (s.lineStyle === 1) ctx.setLineDash([6 * hpr, 4 * hpr]);
        else if (s.lineStyle === 2) ctx.setLineDash([2 * hpr, 3 * hpr]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(endX, endY); ctx.stroke();
        ctx.setLineDash([]);

        // Endpoints
        [x1, y1, x2, y2].length && (() => {
          ctx.fillStyle = s.color;
          ctx.beginPath(); ctx.arc(x1, y1, 3 * hpr, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(x2, y2, 3 * hpr, 0, Math.PI * 2); ctx.fill();
        })();

        if (s.isSelected) {
          drawHandle(ctx, p1.x, p1.y, hpr, vpr);
          drawHandle(ctx, p2.x, p2.y, hpr, vpr);
        }
        ctx.restore();
      });
    }};
  }
}

export class TrendLinePrimitive extends BasePrimitive {
  constructor(point1, point2, options = {}) {
    super();
    this.point1 = point1;
    this.point2 = point2;
    this.color = options.color || '#3b82f6';
    this.extend = options.extend || false;
    if (options.lineWidth != null) this.lineWidth = options.lineWidth;
    if (options.lineStyle != null) this.lineStyle = options.lineStyle;
    this._p1 = null;
    this._p2 = null;
    this._view = new TrendLinePaneView(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    const ts = this._chart.timeScale();
    if (this.point1) this._p1 = { x: ts.logicalToCoordinate(this.point1.logical), y: this._series.priceToCoordinate(this.point1.price) };
    if (this.point2) this._p2 = { x: ts.logicalToCoordinate(this.point2.logical), y: this._series.priceToCoordinate(this.point2.price) };
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    return distToSegment(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x - this._p1.x, y - this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x - this._p2.x, y - this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dLogical, dPrice) {
    this.point1 = { logical: this.point1.logical + dLogical, price: this.point1.price + dPrice };
    this.point2 = { logical: this.point2.logical + dLogical, price: this.point2.price + dPrice };
    this._requestUpdate?.();
  }
  moveHandle(index, logical, price) {
    if (index === 0) this.point1 = { logical, price };
    else this.point2 = { logical, price };
    this._requestUpdate?.();
  }
  setPreviewPoint(point) { this.point2 = point; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// RECTANGLE
// ═══════════════════════════════════════════════════════════════════

class RectanglePaneView {
  constructor(src) { this._s = src; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (target) => {
      target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
        const r = s._rect;
        if (!r) return;
        ctx.save();
        const x = r.x * hpr, y = r.y * vpr, w = r.w * hpr, h = r.h * vpr;

        ctx.fillStyle = hexToRgba(s.borderColor, s.isHovered || s.isSelected ? 0.15 : 0.08);
        ctx.beginPath(); ctx.roundRect(x, y, w, h, 2 * hpr); ctx.fill();

        if (s.isHovered || s.isSelected) {
          ctx.strokeStyle = hexToRgba(s.borderColor, HOVER_ALPHA);
          ctx.lineWidth = 5 * vpr; ctx.stroke();
        }

        ctx.strokeStyle = s.borderColor;
        const lw = s.lineWidth || 1;
        ctx.lineWidth = (s.isSelected ? lw + 1 : lw) * vpr;
        if (s.lineStyle === 1) ctx.setLineDash([6 * hpr, 4 * hpr]);
        else if (s.lineStyle === 2) ctx.setLineDash([2 * hpr, 3 * hpr]);
        else ctx.setLineDash([]);
        ctx.beginPath(); ctx.roundRect(x, y, w, h, 2 * hpr); ctx.stroke();

        if (s.isSelected) {
          [[r.x, r.y], [r.x + r.w, r.y], [r.x, r.y + r.h], [r.x + r.w, r.y + r.h]]
            .forEach(([cx, cy]) => drawHandle(ctx, cx, cy, hpr, vpr));
        }
        ctx.restore();
      });
    }};
  }
}

export class RectanglePrimitive extends BasePrimitive {
  constructor(point1, point2, options = {}) {
    super();
    this.point1 = point1;
    this.point2 = point2;
    this.borderColor = options.borderColor || '#6366f1';
    this.lineWidth = options.lineWidth ?? 1;
    this.lineStyle = options.lineStyle ?? 0;
    this._rect = null;
    this._view = new RectanglePaneView(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    const ts = this._chart.timeScale();
    const x1 = ts.logicalToCoordinate(this.point1.logical);
    const x2 = this.point2 ? ts.logicalToCoordinate(this.point2.logical) : null;
    const y1 = this._series.priceToCoordinate(this.point1.price);
    const y2 = this.point2 ? this._series.priceToCoordinate(this.point2.price) : null;
    if (x1 == null || x2 == null || y1 == null || y2 == null) { this._rect = null; return; }
    this._rect = { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const r = this._rect;
    if (!r) return Infinity;
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return 0;
    return Infinity;
  }
  hitTestHandle(x, y) {
    const r = this._rect;
    if (!r) return -1;
    const corners = [[r.x, r.y], [r.x + r.w, r.y], [r.x, r.y + r.h], [r.x + r.w, r.y + r.h]];
    for (let i = 0; i < corners.length; i++) {
      if (Math.hypot(x - corners[i][0], y - corners[i][1]) < HANDLE_HIT) return i;
    }
    return -1;
  }
  moveBy(dLogical, dPrice) {
    this.point1 = { logical: this.point1.logical + dLogical, price: this.point1.price + dPrice };
    this.point2 = { logical: this.point2.logical + dLogical, price: this.point2.price + dPrice };
    this._requestUpdate?.();
  }
  moveHandle(index, logical, price) {
    // corners: 0=TL, 1=TR, 2=BL, 3=BR  mapped to point1/point2
    const p1 = this.point1, p2 = this.point2;
    const minL = Math.min(p1.logical, p2.logical), maxL = Math.max(p1.logical, p2.logical);
    const minP = Math.min(p1.price, p2.price), maxP = Math.max(p1.price, p2.price);
    if (index === 0) { this.point1 = { logical, price }; this.point2 = { logical: maxL, price: minP }; }
    else if (index === 1) { this.point1 = { logical: minL, price }; this.point2 = { logical, price: minP }; }
    else if (index === 2) { this.point1 = { logical, price: maxP }; this.point2 = { logical: maxL, price }; }
    else { this.point1 = { logical: minL, price: maxP }; this.point2 = { logical, price }; }
    this._requestUpdate?.();
  }
  setPreviewPoint(point) { this.point2 = point; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FIBONACCI
// ═══════════════════════════════════════════════════════════════════

const DEFAULT_FIB_LEVELS = [
  { level: 0,     color: '#808080', visible: true },
  { level: 0.236, color: '#f59e0b', visible: true },
  { level: 0.382, color: '#f97316', visible: true },
  { level: 0.5,   color: '#ef4444', visible: true },
  { level: 0.618, color: '#dc2626', visible: true },
  { level: 0.786, color: '#be185d', visible: true },
  { level: 1,     color: '#22c55e', visible: true },
];

class FibonacciPaneView {
  constructor(src) { this._s = src; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (target) => {
      target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
        const lines = s._lines;
        if (!lines || !lines.length) return;
        const x1 = s._x1, x2 = s._x2;
        if (x1 == null || x2 == null) return;

        // Bounded region: from leftmost point to rightmost point
        const leftX = Math.min(x1, x2) * hpr;
        const rightX = Math.max(x1, x2) * hpr;
        const spanW = rightX - leftX;
        if (spanW < 2) return;

        ctx.save();

        // Zone fills between visible levels
        for (let i = 0; i < lines.length - 1; i++) {
          const y1 = lines[i].y * vpr, y2 = lines[i + 1].y * vpr;
          ctx.fillStyle = hexToRgba(lines[i].color || s.color, 0.06);
          ctx.fillRect(leftX, Math.min(y1, y2), spanW, Math.abs(y2 - y1));
        }

        // Level lines + labels
        for (const line of lines) {
          const yy = line.y * vpr;
          const lvColor = line.color || s.color;
          const isKey = line.level === 0 || line.level === 0.5 || line.level === 1;

          ctx.strokeStyle = hexToRgba(lvColor, s.isHovered || s.isSelected ? 0.9 : 0.6);
          ctx.lineWidth = (isKey ? 1.5 : 1) * vpr;
          ctx.setLineDash(isKey ? [] : [4 * hpr, 3 * hpr]);
          ctx.beginPath(); ctx.moveTo(leftX, yy); ctx.lineTo(rightX, yy); ctx.stroke();

          // Label at right end
          const label = `${(line.level * 100).toFixed(1)}%  (${line.price.toFixed(5)})`;
          ctx.font = `${10 * vpr}px -apple-system, sans-serif`;
          const tw = ctx.measureText(label).width;
          const lblX = rightX + 6 * hpr;
          ctx.setLineDash([]);
          ctx.fillStyle = hexToRgba(lvColor, 0.12);
          ctx.fillRect(lblX, yy - 13 * vpr, tw + 8 * hpr, 15 * vpr);
          ctx.fillStyle = hexToRgba(lvColor, 0.9);
          ctx.textBaseline = 'middle';
          ctx.fillText(label, lblX + 4 * hpr, yy - 5.5 * vpr);
        }

        // Vertical connecting line on left side
        if (lines.length >= 2) {
          const topY = Math.min(...lines.map(l => l.y)) * vpr;
          const botY = Math.max(...lines.map(l => l.y)) * vpr;
          ctx.strokeStyle = hexToRgba(s.color, 0.3);
          ctx.lineWidth = 1 * vpr;
          ctx.setLineDash([]);
          ctx.beginPath(); ctx.moveTo(leftX, topY); ctx.lineTo(leftX, botY); ctx.stroke();
        }

        if (s.isSelected && lines.length >= 2) {
          drawHandle(ctx, Math.min(x1, x2), lines[0].y, hpr, vpr);
          drawHandle(ctx, Math.max(x1, x2), lines[lines.length - 1].y, hpr, vpr);
        }
        ctx.restore();
      });
    }};
  }
}

export class FibonacciPrimitive extends BasePrimitive {
  constructor(point1, point2, options = {}) {
    super();
    this.point1 = point1; // { logical, price }
    this.point2 = point2; // { logical, price }
    this.color = options.color || '#f59e0b';
    this.customLevels = options.levels || DEFAULT_FIB_LEVELS.map(l => ({ ...l }));
    this._lines = [];
    this._x1 = null;
    this._x2 = null;
    this._view = new FibonacciPaneView(this);
  }
  updateAllViews() {
    if (!this._series || !this._chart) return;
    const ts = this._chart.timeScale();
    this._x1 = ts.logicalToCoordinate(this.point1.logical);
    this._x2 = this.point2 ? ts.logicalToCoordinate(this.point2.logical) : this._x1;
    const highPrice = Math.max(this.point1.price, this.point2?.price ?? this.point1.price);
    const lowPrice = Math.min(this.point1.price, this.point2?.price ?? this.point1.price);
    const range = highPrice - lowPrice;
    this._lines = this.customLevels
      .filter(l => l.visible !== false)
      .map((cl) => {
        const price = highPrice - range * cl.level;
        return { level: cl.level, price, y: this._series.priceToCoordinate(price), color: cl.color };
      });
  }
  setLevels(levels) { this.customLevels = levels; this._requestUpdate?.(); }
  getLevels() { return this.customLevels.map(l => ({ ...l })); }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._lines.length || this._x1 == null || this._x2 == null) return Infinity;
    const left = Math.min(this._x1, this._x2), right = Math.max(this._x1, this._x2);
    if (x < left - HIT_TOLERANCE || x > right + HIT_TOLERANCE) return Infinity;
    return Math.min(...this._lines.map((l) => Math.abs(y - l.y)));
  }
  hitTestHandle(x, y) {
    if (!this._lines.length || this._x1 == null) return -1;
    const lx = Math.min(this._x1, this._x2), rx = Math.max(this._x1, this._x2);
    if (Math.hypot(x - lx, y - this._lines[0].y) < HANDLE_HIT) return 0;
    if (Math.hypot(x - rx, y - this._lines[this._lines.length - 1].y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dLogical, dPrice) {
    this.point1 = { logical: this.point1.logical + dLogical, price: this.point1.price + dPrice };
    if (this.point2) this.point2 = { logical: this.point2.logical + dLogical, price: this.point2.price + dPrice };
    this._requestUpdate?.();
  }
  moveHandle(index, logical, price) {
    if (index === 0) this.point1 = { logical, price };
    else if (this.point2) this.point2 = { logical, price };
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.point2 = pt; this._requestUpdate?.(); }
}
